import type { Json } from "@/integrations/supabase/types";
/**
 * Whether a topic's content generation actually finished.
 *
 * A topic is created first and its content is streamed in afterwards, so an
 * interrupted run (closed tab, failed request, cancelled queue) leaves a row
 * that looks like an ordinary topic but has nothing — or only half of the
 * content — to practise. The list only has question/vocabulary COUNTS (see
 * `use-practice-sessions`), which is exactly enough to see that a run stopped:
 * the generation pipeline always produces both parts, so either one at zero
 * means it never completed.
 *
 * The speaking guide lives in a JSONB column on the session row and is not
 * fetched for the list, so it is deliberately left out here. The detail page
 * owns that check (`lib/topic-status.ts`) once it has the full row, and the
 * "finish generating" flow there re-reads the status before choosing what to
 * regenerate.
 */
export type TopicContentGap = "questions" | "vocabulary";

export interface TopicGenerationState {
    /** At least one expected part is missing. */
    isIncomplete: boolean;
    /** Nothing was produced at all — the run stopped before the first phase. */
    isBlank: boolean;
    /** The parts that are missing, in pipeline order. */
    missing: TopicContentGap[];
}

const countRows = (value?: Json | null): number =>
    Array.isArray(value) ? value.length : 0;

export const getTopicGenerationState = (
    session?: { questions?: Json | null; vocabulary?: Json | null } | null
): TopicGenerationState => {
    const questionCount = countRows(session?.questions);
    const vocabularyCount = countRows(session?.vocabulary);

    const missing: TopicContentGap[] = [];
    if (questionCount === 0) missing.push("questions");
    if (vocabularyCount === 0) missing.push("vocabulary");

    return {
        isIncomplete: missing.length > 0,
        isBlank: questionCount === 0 && vocabularyCount === 0,
        missing,
    };
};
